/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, Table, Pagination, Tag, message } from 'antd';
import { services } from '@comall-backend-builder/core';
import { errorHandle } from '../../services';
import './spu-select-modal.less';

const api = services.api;

/** 新增接口 spuList 入参字段 */
export type SpuRelItem = {
    spuCode: string;
    goodsName: string;
    skuCount?: number;
};

type SpuSelectModalProps = {
    visible: boolean;
    defaultSelected?: SpuRelItem[];
    /** 归属当前规则的 SPU（含已从列表移除的），hasRichText 时仍可再次勾选 */
    ownedSpuCodes?: string[];
    onCancel: () => void;
    onOk: (selected: SpuRelItem[]) => void;
};

/** find_spu 列表行 */
type FindSpuRow = {
    spuCode: string;
    goodsName: string;
    brand?: string;
    skuCount?: number;
    hasRichText?: boolean;
};

const toSpuRelItem = (item: any): SpuRelItem => ({
    spuCode: item.spuCode || item.spu || '',
    goodsName: item.goodsName || item.name || '',
    skuCount: item.skuCount,
});

const toFindSpuRow = (item: any): FindSpuRow => ({
    spuCode: item.spuCode || item.spu || '',
    goodsName: item.goodsName || item.name || '',
    brand: item.brand || item.brandName || '-',
    skuCount: item.skuCount,
    hasRichText: !!item.hasRichText,
});

export const BatchDetailSpuSelectModal: React.FC<SpuSelectModalProps> = ({
    visible,
    defaultSelected = [],
    ownedSpuCodes = [],
    onCancel,
    onOk,
}) => {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<FindSpuRow[]>([]);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [spuKeyword, setSpuKeyword] = useState('');
    const [nameKeyword, setNameKeyword] = useState('');
    const [searchSpu, setSearchSpu] = useState('');
    const [searchName, setSearchName] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [allSelected, setAllSelected] = useState<SpuRelItem[]>([]);
    /** 打开弹窗时列表中已圈选：禁用，只能通过列表移除 */
    const [selectedInListSet, setSelectedInListSet] = useState<Set<string>>(new Set());
    /** 归属当前规则：移除后仍可再次勾选 */
    const [ownedCodeSet, setOwnedCodeSet] = useState<Set<string>>(new Set());

    const loadProducts = (
        page = pageNum,
        size = pageSize,
        spu = searchSpu,
        name = searchName,
        selected = allSelected
    ) => {
        setLoading(true);
        const params: Record<string, any> = {
            page,
            per_page: size,
        };
        const trimmedSpu = (spu || '').trim();
        const trimmedName = (name || '').trim();
        if (trimmedSpu) {
            params.spu = trimmedSpu;
        }
        if (trimmedName) {
            params.name = trimmedName;
        }

        api.get(params, { apiPath: '/products/search/find_spu_rich_text' })
            .then((res: any) => {
                const list = (res?.result || []).map(toFindSpuRow);
                const keys = list
                    .filter((product: FindSpuRow) =>
                        selected.some((item) => item.spuCode === product.spuCode)
                    )
                    .map((product: FindSpuRow) => product.spuCode);
                setProducts(list);
                setTotal(res?.totalNum || res?.total_num || 0);
                setSelectedRowKeys(keys);
            })
            .catch(errorHandle)
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (!visible) {
            return;
        }
        const defaults = (defaultSelected || []).map(toSpuRelItem);
        setAllSelected(defaults);
        setSelectedRowKeys(defaults.map((item) => item.spuCode));
        setSelectedInListSet(new Set(defaults.map((item) => item.spuCode).filter(Boolean)));
        setOwnedCodeSet(new Set((ownedSpuCodes || []).filter(Boolean)));
        setSpuKeyword('');
        setNameKeyword('');
        setSearchSpu('');
        setSearchName('');
        setPageNum(1);
        setPageSize(10);
        loadProducts(1, 10, '', '', defaults);
    }, [visible]);

    const handleSearch = () => {
        setSearchSpu(spuKeyword);
        setSearchName(nameKeyword);
        setPageNum(1);
        loadProducts(1, pageSize, spuKeyword, nameKeyword);
    };

    /**
     * 已在列表中 → 禁用（只能列表移除）
     * 已有规则且非当前单据 → 禁用
     * 已有规则但是当前单据（含已移除）→ 可再次勾选
     */
    const isRowDisabled = (record: FindSpuRow) => {
        if (selectedInListSet.has(record.spuCode)) {
            return true;
        }
        if (record.hasRichText && !ownedCodeSet.has(record.spuCode)) {
            return true;
        }
        return false;
    };

    const handleRowSelectionChange = (keys: string[] | number[], rows: FindSpuRow[]) => {
        // 已圈选的行保持勾选，不允许在弹窗内取消
        const forcedKeys = products
            .filter((product) => selectedInListSet.has(product.spuCode))
            .map((product) => product.spuCode);
        const selectedKeys = Array.from(
            new Set([...(keys as Array<string | number>).map((key) => String(key)), ...forcedKeys])
        );
        const currentPageUnselected = products
            .filter(
                (product) =>
                    !selectedKeys.includes(product.spuCode) &&
                    !selectedInListSet.has(product.spuCode)
            )
            .map((product) => product.spuCode);

        const kept = allSelected.filter(
            (product) => !currentPageUnselected.includes(product.spuCode)
        );
        const newlyAdded = rows
            .filter(
                (product) =>
                    !isRowDisabled(product) &&
                    !kept.some((selected) => selected.spuCode === product.spuCode)
            )
            .map(toSpuRelItem);

        setSelectedRowKeys(selectedKeys);
        setAllSelected([...kept, ...newlyAdded]);
    };

    const handleOk = () => {
        if (!allSelected.length) {
            message.warning('请至少选择一个 SPU');
            return;
        }
        onOk(allSelected);
    };

    const columns = [
        {
            title: 'SPU',
            dataIndex: 'spuCode',
            key: 'spuCode',
            width: 160,
        },
        {
            title: '商品名称',
            dataIndex: 'goodsName',
            key: 'goodsName',
            ellipsis: true,
        },
        {
            title: '品牌',
            dataIndex: 'brand',
            key: 'brand',
            width: 120,
            render: (text: string) => text || '-',
        },
        {
            title: 'SKU 数',
            dataIndex: 'skuCount',
            key: 'skuCount',
            width: 90,
            render: (val: number) => (val != null ? val : '-'),
        },
        {
            title: '占用',
            dataIndex: 'hasRichText',
            key: 'hasRichText',
            width: 110,
            render: (hasRichText: boolean) =>
                hasRichText ? (
                    <Tag className="batch-detail-spu-select__occupied-tag">已有规则</Tag>
                ) : (
                    '-'
                ),
        },
    ];

    return (
        <Modal
            title="选择 SPU"
            visible={visible}
            onCancel={onCancel}
            width={860}
            destroyOnClose
            className="batch-detail-spu-select"
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    取消
                </Button>,
                <Button key="ok" type="primary" onClick={handleOk}>
                    确认选择 ({allSelected.length})
                </Button>,
            ]}
        >
            <div className="batch-detail-spu-select__search">
                <Input
                    allowClear
                    placeholder="SPU"
                    value={spuKeyword}
                    onChange={(e) => setSpuKeyword(e.target.value)}
                    onPressEnter={handleSearch}
                    style={{ width: 200 }}
                />
                <Input
                    allowClear
                    placeholder="商品名称"
                    value={nameKeyword}
                    onChange={(e) => setNameKeyword(e.target.value)}
                    onPressEnter={handleSearch}
                    style={{ width: 240 }}
                />
                <Button type="primary" onClick={handleSearch}>
                    搜索
                </Button>
            </div>

            <Table
                rowKey="spuCode"
                columns={columns}
                dataSource={products}
                loading={loading}
                pagination={false}
                scroll={{ y: 420 }}
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys,
                    onChange: handleRowSelectionChange,
                    getCheckboxProps: (record: FindSpuRow) => ({
                        disabled: isRowDisabled(record),
                    }),
                }}
            />

            {total > 0 && (
                <div className="batch-detail-spu-select__pagination">
                    <Pagination
                        current={pageNum}
                        pageSize={pageSize}
                        total={total}
                        showSizeChanger={false}
                        onChange={(page, size) => {
                            setPageNum(page);
                            setPageSize(size || pageSize);
                            loadProducts(page, size || pageSize, searchSpu, searchName);
                        }}
                    />
                </div>
            )}
        </Modal>
    );
};

export default BatchDetailSpuSelectModal;
